import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotate, faPlus } from "@fortawesome/free-solid-svg-icons";
import CustomTable from "../../CommonComponents/Table/CustomTable";
import { networkService } from "../../config/axios";
import Paginate from "../../CommonComponents/Pagination/Paginate";
import LoadingScreen from "../../Elements/LoadingScreen/LoadingScreen";
import CommonModal from "../../Modals/CommonModal";
import SupportModal from "../../Modals/SupportModal";
import UserInfoContext from "../../../utils/userInfoContext";
import "./SupportPage.css";

const SupportPage = () => {
    const [tickets, setTickets] = useState({});
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [openSupport, setOpenSupport] = useState(false);
    const { setShowNotifications } = useContext(UserInfoContext);
    const isFetchingRef = useRef(false);
    const initialLoadRef = useRef(false);

    const fetchTickets = useCallback(async (pageToFetch = 1) => {
        if (isFetchingRef.current) {
            return;
        }
        isFetchingRef.current = true;
        setLoading(true);
        try {
            const response = await networkService(`feedbacks/tickets?page=${pageToFetch}&per_page=10`, "GET");
            setTickets(response?.data || {});
        } catch (error) {
            console.error("Error fetching tickets:", error);
        } finally {
            setLoading(false);
            isFetchingRef.current = false;
        }
    }, []);

    // Pulls the current status straight from Redmine and writes it into our
    // DB before the table is (re)loaded, so opening/refreshing the page
    // never shows a stale, previously-synced status.
    const syncStatusesFromRedmine = useCallback(async () => {
        try {
            const response = await networkService("feedbacks/refresh_statuses", "POST", {});
            return response?.data || {};
        } catch (error) {
            console.error("Error syncing ticket statuses from Redmine:", error);
            setShowNotifications({
                showToast: true,
                toastType: "danger",
                toastMessage: "Couldn't reach Redmine to refresh statuses. Showing the last known status.",
            });
            return null;
        }
    }, [setShowNotifications]);

    useEffect(() => {
        if (initialLoadRef.current || isFetchingRef.current) {
            return;
        }
        initialLoadRef.current = true;
        syncStatusesFromRedmine().finally(() => fetchTickets(page));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handlePageChange = (newPage = 1) => {
        setPage(newPage);
        fetchTickets(newPage);
    };

    const handleTicketAdded = () => {
        setPage(1);
        fetchTickets(1);
    };

    const handleRefreshStatuses = async () => {
        setRefreshing(true);
        try {
            const synced = await syncStatusesFromRedmine();
            await fetchTickets(page);
            if (synced) {
                const retried = synced.retried_ticket_creation || 0;
                setShowNotifications({
                    showToast: true,
                    toastType: "success",
                    toastMessage: retried > 0
                        ? `Ticket statuses are up to date. Retrying ID assignment for ${retried} ${retried > 1 ? "tickets" : "ticket"}.`
                        : "Ticket statuses are up to date.",
                });
            }
        } finally {
            setRefreshing(false);
        }
    };

    const columns = React.useMemo(() => {
        return [
            {
                Header: "Category",
                accessor: "category_label",
            },
            {
                Header: "Description",
                accessor: "happened_text",
                Cell: ({ row }) => (
                    <span className="ticket-description-preview" title={row?.original?.happened_text}>
                        {row?.original?.happened_text || "-"}
                    </span>
                ),
            },
            {
                Header: "Ticket ID",
                accessor: "redmine_issue_id",
                Cell: ({ row }) => row?.original?.redmine_issue_id || "Assigning soon",
            },
            {
                Header: "Status",
                accessor: "status_label",
                Cell: ({ row }) => (
                    <span className={`ticket-status-badge status-${row?.original?.status}`}>
                        {row?.original?.status_label}
                    </span>
                ),
            },
            {
                Header: "Created",
                accessor: "created_at",
                Cell: ({ row }) => (
                    row?.original?.created_at
                        ? new Date(row.original.created_at).toLocaleDateString()
                        : "-"
                ),
            },
            {
                Header: "",
                accessor: "actions",
                Cell: ({ row }) => (
                    <span className="ticket-view-link" onClick={() => setSelectedTicket(row?.original)}>
                        View
                    </span>
                ),
            },
        ];
    }, []);

    return (
        <>
            {loading && <LoadingScreen />}

            <CommonModal
                title="Ticket Details"
                modalStatus={!!selectedTicket}
                changeActiveStatus={() => setSelectedTicket(null)}
                showFooter={false}
            >
                {selectedTicket && (
                    <div className="ticket-details-grid">
                        <div className="ticket-details-row">
                            <span className="ticket-details-label">Category</span>
                            <span className="ticket-details-value">{selectedTicket.category_label}</span>
                        </div>
                        <div className="ticket-details-row">
                            <span className="ticket-details-label">Status</span>
                            <span className={`ticket-status-badge status-${selectedTicket.status}`}>
                                {selectedTicket.status_label}
                            </span>
                        </div>
                        <div className="ticket-details-row">
                            <span className="ticket-details-label">Redmine Ticket ID</span>
                            <span className="ticket-details-value">{selectedTicket.redmine_issue_id || "Assigning soon"}</span>
                        </div>
                        <div className="ticket-details-row">
                            <span className="ticket-details-label">Submitted On</span>
                            <span className="ticket-details-value">
                                {selectedTicket.created_at ? new Date(selectedTicket.created_at).toLocaleString() : "-"}
                            </span>
                        </div>
                        {selectedTicket.url && (
                            <div className="ticket-details-row">
                                <span className="ticket-details-label">Page URL</span>
                                <span className="ticket-details-value">{selectedTicket.url}</span>
                            </div>
                        )}
                        <div className="ticket-details-row">
                            <span className="ticket-details-label">What I did</span>
                            <span className="ticket-details-value">{selectedTicket.did_text || "-"}</span>
                        </div>
                        <div className="ticket-details-row">
                            <span className="ticket-details-label">What I expected</span>
                            <span className="ticket-details-value">{selectedTicket.expected_text || "-"}</span>
                        </div>
                        <div className="ticket-details-row">
                            <span className="ticket-details-label">What happened</span>
                            <span className="ticket-details-value">{selectedTicket.happened_text || "-"}</span>
                        </div>
                    </div>
                )}
            </CommonModal>

            <div className="header-content-main tickets-header">
                <button
                    className="tickets-add-btn"
                    onClick={() => setOpenSupport(true)}
                >
                    <FontAwesomeIcon icon={faPlus} />
                    Add Ticket
                </button>
                <button
                    className="tickets-refresh-btn"
                    onClick={handleRefreshStatuses}
                    disabled={refreshing}
                >
                    <FontAwesomeIcon icon={faRotate} className={refreshing ? "fa-spin" : ""} />
                    {refreshing ? "Refreshing..." : "Refresh"}
                </button>
            </div>

            {openSupport && (
                <SupportModal
                    openSupport={openSupport}
                    setOpenSupport={setOpenSupport}
                    onSubmitted={handleTicketAdded}
                />
            )}
            {!loading && (
                <div className="tickets-table-card">
                    <CustomTable data={tickets?.feedbacks || []} columnData={columns} showFilters={false} />
                    {tickets?.total_feedbacks > 10 && (
                        <Paginate
                            currentPage={page}
                            totalPages={Math.ceil(tickets?.total_feedbacks / 10)}
                            onPageChange={handlePageChange}
                            totalData={tickets?.total_feedbacks}
                            itemTitle={tickets?.total_feedbacks > 1 ? "Tickets" : "Ticket"}
                            pageSize={10}
                            currentCount={tickets?.feedbacks?.length}
                        />
                    )}
                </div>
            )}
        </>
    );
};

export default SupportPage;
